import { Spacing } from '@persona-id/design-tokens';
import { compact, countBy, identity, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import Pii from 'dashboard/components/Pii';
import {
  MatchIconAlert,
  MatchIconDismissed,
  MatchIconPositive,
} from 'dashboard/components/ReportResult/MatchIcons';
import {
  AdverseMediaType,
  adverseMediaTypeDisplayText,
  MatchLevel,
  matchLevelShortDisplayText,
  RelatedSource,
} from 'dashboard/models/report/adverse-media';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { paragraphMixins } from 'design-system/mixins/text';
import theme from 'design-system/theme';
import IconChevron from 'images/icons/chevron.svg';
import { COUNTRIES } from 'lib/constants/locales';

const SUBTEXT_SEP = ' · ';
const MAX_AGE = 120;

const StyledIconChevron = styled(IconChevron)`
  width: ${Spacing[5]};
  flex-shrink: 0;
  align-self: center;
`;

const MatchSummaryContent = styled('div')`
  flex-grow: 1;
`;

const MatchTitle = styled('div')`
  ${paragraphMixins.lgBold};
  color: ${theme.color.text.primary};
  line-height: 26px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Subtext = styled('div')`
  ${paragraphMixins.md};
  display: flex;
  color: ${COLORS.gray70};
`;

const Stickers = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const StyledSticker = styled(Sticker)`
  margin-right: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
`;

const Container = styled.div`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: ${Spacing[5]} 1fr ${Spacing[5]};
  padding: ${Spacing[2]} 0;
`;

const AdverseMediaIdentityMatchSummary = ({
  relatedSource,
  isDismissed = false,
}: {
  relatedSource: RelatedSource;
  className?: string;
  isDismissed?: boolean;
}) => {
  const { name, sources, birthdates, 'match-types': matchTypes } = relatedSource;

  const matchText = matchTypes
    ? compact(matchTypes.map((t) => matchLevelShortDisplayText(t as MatchLevel))).join(', ')
    : '';
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const countries = uniq(sources.flatMap((source) => source['country-codes']));
  const countriesText =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    countries.length > 0 ? countries.map((code) => COUNTRIES[code]).join(', ') : '';
  let ageText = '';
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (birthdates?.length > 0) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const age = Math.floor(DateTime.local().diff(DateTime.fromISO(birthdates[0]), 'years').years);
    if (age <= MAX_AGE) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      switch (birthdates.length) {
        case 1:
          ageText = `Age: ${age}`;
          break;
        case 2:
          ageText = `Age: ${age} (and 1 other)`;
          break;
        default:
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ageText = `Age: ${age} (and ${birthdates.length - 2} others)`;
      }
    }
  }

  const subText = compact([matchText, ageText, countriesText]).join(SUBTEXT_SEP);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const adverseMediaTypesWithFreq = countBy(identity)(sources.flatMap((source) => source['types']));

  let icon;
  if (isDismissed) {
    icon = <MatchIconDismissed />;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  } else if (relatedSource.media.length === 0) {
    icon = <MatchIconPositive />;
  } else {
    icon = <MatchIconAlert />;
  }

  return (
    <Container>
      {icon}
      <MatchSummaryContent>
        <Pii isRedacted={false}>
          <MatchTitle>{name}</MatchTitle>
        </Pii>
        <Spacer height={1} />
        <Subtext>{subText}</Subtext>
        <Spacer height={1} />
        <Stickers>
          {Object.entries(adverseMediaTypesWithFreq).map(([t, f]) => (
            <StyledSticker key={t}>{`${adverseMediaTypeDisplayText(t as AdverseMediaType)} ${
              f > 1 ? f : ''
            }`}</StyledSticker>
          ))}
        </Stickers>
      </MatchSummaryContent>
      <StyledIconChevron />
    </Container>
  );
};

export default AdverseMediaIdentityMatchSummary;
